import { z } from "zod";
import { getCategory } from "./categories";
import { RequestStatusSchema } from "./enums";
import { StaffRaiseBodySchema, StaffRaiseResponseSchema } from "./staff-api";

export const FreeScoutSyncStateSchema = z.enum([
  "unlinked",
  "linked",
  "needs_review",
  "unavailable",
]);
export const FreeScoutReferenceSchema = z
  .object({
    instanceId: z.literal("primary"),
    mailboxId: z.literal(2),
    conversationId: z.number().int().positive().max(2_147_483_647),
  })
  .strict();
export type FreeScoutReference = z.infer<typeof FreeScoutReferenceSchema>;
export const FreeScoutStatusSchema = FreeScoutReferenceSchema.extend({
  syncState: FreeScoutSyncStateSchema,
  requests: z
    .array(
      z
        .object({
          id: z.uuid(),
          publicId: z.string().regex(/^HOF-\d+$/),
          status: RequestStatusSchema,
          portalUrl: z.url(),
          boardUrl: z.url().nullable(),
        })
        .strict(),
    )
    .max(1),
  checkedAt: z.iso.datetime().nullable(),
}).strict();
export type FreeScoutStatus = z.infer<typeof FreeScoutStatusSchema>;
export const FreeScoutPreviewSchema = FreeScoutReferenceSchema.extend({
  subject: z.string(),
  senderEmail: z.email().nullable(),
  sourceThreads: z.array(z.object({ id: z.number().int().positive(), createdAt: z.string() })),
  conversationUrl: z.url(),
  status: FreeScoutStatusSchema,
}).strict();
export type FreeScoutPreview = z.infer<typeof FreeScoutPreviewSchema>;
/** A permanent conversation mapping can replay after the request reaches the board. */
export const FreeScoutCreateResponseSchema = StaffRaiseResponseSchema.extend({
  onBoard: z.boolean(),
});
export type FreeScoutCreateResponse = z.infer<typeof FreeScoutCreateResponseSchema>;
export const FreeScoutCreateSchema = z
  .object({
    requesterUserId: z.uuid(),
    sourceThreadId: z.number().int().positive(),
    request: StaffRaiseBodySchema,
  })
  .strict()
  .superRefine((body, ctx) => {
    if (!getCategory(body.request.category)?.portalReceivable) {
      ctx.addIssue({
        code: "custom",
        path: ["request", "category"],
        message: "Use the verified phone intake process for this category",
      });
    }
    if (body.request.caller.verification.trim().length < 10) {
      ctx.addIssue({
        code: "custom",
        path: ["request", "caller", "verification"],
        message: "Record how you verified this requester (at least 10 characters)",
      });
    }
  });
export const FreeScoutLinkSchema = z
  .object({
    requestId: z.uuid(),
    requesterUserId: z.uuid(),
    sourceThreadId: z.number().int().positive(),
    verification: z.string().trim().min(10).max(500),
  })
  .strict();
export const FreeScoutRepairSchema = FreeScoutReferenceSchema.extend({
  sourceThreadId: z.number().int().positive(),
  requesterUserId: z.uuid(),
  version: z.number().int().positive(),
  verification: z.string().trim().min(10).max(500),
}).strict();
export const FreeScoutRequesterListSchema = z
  .object({
    requesters: z
      .array(
        z
          .object({
            id: z.uuid(),
            upn: z.string(),
            displayName: z.string().nullable(),
            tenantId: z.uuid(),
          })
          .strict(),
      )
      .max(20),
  })
  .strict();
export const FreeScoutMappingSchema = FreeScoutReferenceSchema.extend({
  id: z.uuid(),
  sourceThreadId: z.number().int().positive(),
  requestId: z.uuid(),
  verifiedRequesterId: z.uuid(),
  syncState: FreeScoutSyncStateSchema,
  lastError: z.string().nullable(),
  checkedAt: z.iso.datetime().nullable(),
  version: z.number().int().positive(),
}).strict();
export const FreeScoutMappingListSchema = z
  .object({ mappings: z.array(FreeScoutMappingSchema) })
  .strict();
